
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import SegmentedBar from "../components/SegmentedBar";
import { API_PATH } from "../environments";

import GradientLayout from "../components/GradientLayout";
import { color } from "framer-motion";


const domains = ["DevOps", "SRE"];

function getUserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function getGreeting(timeZone) {
  const now = new Date();
  const userTimeStr = now.toLocaleString("en-US", {
    timeZone,
    hour12: false,
    hour: "2-digit",
  });
  const userHour = parseInt(userTimeStr, 10);

  if (userHour >= 5 && userHour < 12) return "Good Morning!";
  if (userHour >= 12 && userHour < 17) return "Good Afternoon!";
  return "Good Night!";
}

const maturityColorMap = {
  Initial: "#C71313",
  Managed: "#FBBC05",
  Measured: "#008200",
  Optimized: "#0F62FE",
};

const maturityBgMap = {
  Initial: "#FFE9E7",
  Managed: "#FFF8F1",
  Measured: "#F5F6F8",
  Optimized: "#EDF0FD",
};


export default function RecommendationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState({ name: "", email: "" });
  const [currentDomain, setCurrentDomain] = useState("SRE");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);



  const [evaluationId, setEvaluationId] = useState(null);
  const {
    recommendationBg,
    recommendationTextColor,
    maturityLevel = "--",

    // ✅ ADD
  } = location.state || {};

  const params = new URLSearchParams(location.search);

  const createdAt =
    params.get("createdAt") || location.state?.createdAt || null;
  const formattedDateTime = createdAt
    ? new Date(createdAt).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    })
    : "--";

  const maturityTextColor =
    maturityColorMap[maturityLevel] || "#202225";
  const headerBgColor = maturityBgMap[maturityLevel] || "#F5F6F8";

  useEffect(() => {
    if (location.state?.category) setSelectedCategory(location.state.category);
    if (location.state?.score) setSelectedScore(location.state.score);
    if (location.state?.domain) setCurrentDomain(location.state.domain);

    const params = new URLSearchParams(location.search);
    const nameParam = params.get("name") || location.state?.name;
    const emailParam = params.get("email") || location.state?.email;
    const domainParam = params.get("domain") || location.state?.domain;
    const evalParam = params.get("evaluationId") || location.state?.evaluationId; // ✅ added line

    if (nameParam || emailParam) {
      setUser({
        name: nameParam || "Guest",
        email: emailParam || "no-email@example.com",
      });
    }

    if (domainParam) {
      const normalizedDomain = domains.find(
        (d) => d.toLowerCase() === domainParam.toLowerCase()
      );
      setCurrentDomain(normalizedDomain || "SRE");
    }

    if (evalParam) {
      setEvaluationId(evalParam); // ✅ added line
    }
  }, [location.search, location.state]);


  // Fetch recommendations only if score < 100
  useEffect(() => {
    const evalParam = params.get("evaluationId") || location.state?.evaluationId;
    if (!currentDomain || !selectedCategory) return;

    // if (selectedScore === 100) {
    //   setRecommendations([]);
    //   setLoading(false);
    //   return;
    // }

    setLoading(true);
    // http://localhost:8085/api/evaluations/user/${encodeURIComponent
    fetch(
       `${API_PATH}/evaluations/user/${encodeURIComponent(
        user.email
      )}/domain/${currentDomain}`,{credentials:'include'}
    )

      .then((res) => res.json())
      .then((data) => {
        // const answers = data[0]?.answers || [];
        const answers = data?.filter((item) => item?.evaluationId == evalParam);
        const filteredRecs = answers[0]?.answers
          .filter((item) => item.category === selectedCategory)
          .map((item) => item.recommendation || "No recommendation");
        setRecommendations(filteredRecs);
      })
      .catch((err) => {
        console.error("Error fetching recommendations:", err);
        setRecommendations([]);
      })
      .finally(() => setLoading(false));
  }, [currentDomain, selectedCategory, selectedScore]);

  return (
    <>

      {/* Main Content Container */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "72%", lg: "93%", xl: "95%" },
          height: { xs: "auto", sm: "75%", md: "75%", lg: "80%", xl: "85%" },
          boxSizing: "border-box",
          overflowX: "hidden",
          alignSelf: { xs: "flex-start", md: "stretch" },
          borderRadius: "16px",
          background: "white",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(15px)",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          marginTop: 2,
          padding: { xs: "1rem", sm: 3 },
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Typography
          sx={{
            alignSelf: "stretch",
            color: "var(--Text-Text-primary, #202225)",
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontFamily: "var(--Dashboard-Cards-Card-Title-Font, Akkurat-Bold)",
            fontSize: "var(--Dashboard-Cards-Card-Title-Size, 1rem)",
            fontStyle: "normal",
            fontWeight: 400,
            // lineHeight: "var(--Dashboard-Cards-Card-Title-Line-height, 1.5rem)",
            // letterSpacing: "var(--Dashboard-Cards-Card-Title-Spacing, 0)",
            mb:0.5
          }}
        >
          Maturity Level Assessment Recommendations
        </Typography>


        {/* Header Box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "0.5rem",
            gap: "1.5rem",
            mb:0.5,
            alignSelf: "stretch",
            borderRadius: "0.5rem",
            backgroundColor: headerBgColor, // ✅ dynamic background
          }}
        >
          <Typography
            sx={{
              color: maturityTextColor,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: '"Akkurat-Pro"',
              fontSize: "1rem",
              fontWeight: 400,
            }}
          >
            Recommendation:
          </Typography>

          <Typography
            sx={{
              color: maturityColorMap[maturityLevel] || "#000",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "Akkurat-Bold",
              fontSize: "1rem",
              fontWeight: 400,
            }}
          >
            {maturityLevel}
          </Typography>
        </Box>
        <Card
          sx={{
            width: "100%",
            borderRadius: "1rem",
            border: "1px solid #C0C7CF",
            backgroundColor: "#FFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

            /* ✅ KEY */
            height: "auto",
            overflow: "visible",
          }}
        >

          <CardContent
            sx={{
              padding: "1rem",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
              columnGap: "1.5rem",
              rowGap: "0.75rem",
              alignItems: "start",
              overflow: "visible",
            }}
          >
            {/* ───────── LEFT COLUMN ───────── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {/* Maturity Badge */}
              <Box
                sx={{
                  width: "fit-content",
                  // padding: "0.25rem 0.6rem",

                  borderRadius: "0.5rem",
                  backgroundColor: "#F5F6F8",
                  borderRadius: " var(--radius-small, 0.25rem)",

                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "Akkurat-Bold",
                    color: maturityColorMap[maturityLevel],
                  }}
                >
                  {maturityLevel}
                </Typography>
              </Box>
              {/* Assessment ID */}
              <Typography sx={{ fontSize: "0.75rem", color: "#6F6F6F" }}>
                Assessment ID
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                {evaluationId}
              </Typography>
              {/* Date & Time */}
              <Typography sx={{ fontSize: "0.75rem", color: "#6F6F6F" }}>
                Date & Time:
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#202225" }}>
                {formattedDateTime}
              </Typography>
            </Box>
            {/* ───────── RIGHT COLUMN ───────── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {/* TOP ROW: Progress + Score */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <SegmentedBar score={selectedScore || 0} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontFamily: "Akkurat-Bold",
                    color: maturityColorMap[maturityLevel],
                    whiteSpace: "nowrap",
                  }}
                >
                  {Math.round(selectedScore)}%
                </Typography>
              </Box>
              {/* Category */}
              <Typography
                sx={{
                  fontFamily: "Akkurat-Bold",
                  fontSize: "1rem",
                  color: "#202225",
                }}
              >
                {selectedCategory}
              </Typography>

              {/* Description */}
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#3C4044",
                  lineHeight: "1.4",
                }}
              >
                Your security level is currently {Math.round(selectedScore)}%, which is
                considered <b>{maturityLevel}</b>.
                <br />
                <b>Maturity Level Definition:</b> System is following best practices with
                tooling in place.
              </Typography>
            </Box>
          </CardContent>
        </Card>
        {/* Recommendation Section */}
        {loading ? (
          <CircularProgress sx={{ color: "white" }} />
        ) :recommendations.length > 0 ? (
          <Box
          sx={{
            /* ✅ OUTER WRAPPER — YOUR CSS (UNCHANGED) */
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "1rem",
            alignSelf: "stretch",
            padding: "1rem",
            borderRadius: "0.75rem",
            border: `1.5px solid ${
              maturityColorMap[maturityLevel] || "#C0C7CF"
            }`,
            background: "var(--Background-Background, #FFF)",
        
            /* scrolling stays same */
            maxHeight: { xs: "40vh", sm: "50vh", md: "55vh" },
            overflowY: "auto",
            overflowX: "hidden",
        
            "&::-webkit-scrollbar": { width: "0.4rem" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.25)",
              borderRadius: "8px",
            },
          }}
        >
          {[...new Set(recommendations)].slice(0, 5).map((r, i, arr) => (
            <Box key={i} sx={{ width: "100%" }}>
              {/* Recommendation Content */}
              <Typography
                sx={{
                  color: "#3C4044",
                  fontSize: "0.85rem",
                  fontWeight: 400,
                }}
              >
                {r.trim()}
              </Typography>
        
              {/* ✅ Thin grey separator */}
              {i < arr.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    backgroundColor: "#E0E0E0",
                    margin: "5px 0",
                    width: "100%",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
        
        )
          : (
            <Typography sx={{ color: "#3C4044" }}>
              No recommendations available.
            </Typography>
          )}

        <Button
          variant="outlined"
          onClick={() =>
            navigate(
              `/sre-score?evaluationId=${evaluationId}&domain=${currentDomain}&createdAt=${encodeURIComponent(
                createdAt
              )}&name=${encodeURIComponent(user.name)}&email=${encodeURIComponent(
                user.email
              )}`
            )
          }
          sx={{
            display: "flex",
            width: "7.5rem",
            height: "2.5rem",
            // padding: "var(--sds-size-space-200)", // padding/small
            justifyContent: "center",
            alignItems: "center",
            // gap: "var(--sds-size-space-050)",

            borderRadius: "1.75rem",
            border: "1px solid #1F2D5C",

            backgroundColor: "#FFFFFF",
            color: "#1F2D5C",

            fontWeight: 600,
            fontSize: "0.8rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#FFFFFF",
              border: "1px solid #1F2D5C",
            },
          }}
        >
          Back
        </Button>
      </Box>
    </>
  );
}